"use client";

import {
    Gift,
    Mail,
    Sparkles,
    CalendarDays,
    CreditCard,
    Check,
} from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    useLanguage,
} from "@/components/LanguageContext";

const translations = {
    hu: {
        heroTitle:
            "ajándékozz strike élményt",

        heroSubtitle:
            "amikor valami többet adnál egy tárgynál.",

        heroDescription:
            "a strike ajándékkártyával nem kell eldöntened, mikor jöjjön, kivel játsszon vagy melyik élményt válassza. ezt majd ő eldönti.",

        heroSecond:
            "válassz egy összeget, vásárold meg online, és már kész is az ajándék.",

        purchaseButton:
            "ajándékkártyát vásárolok",

        giftTitle:
            "egy ajándék, amiből élmény lesz",

        giftFirst:
            "van, amikor nehéz kitalálni, minek örülne igazán valaki.",

        giftSecond:
            "egy strike ajándékkártyával nem egy újabb tárgyat adsz, hanem lehetőséget egy közös programra, játékra és versenyre.",

        giftThird:
            "az ajándékozott pedig akkor használja fel, amikor neki a legjobb.",

        howTitle:
            "hogyan működik?",

        howIntroduction:
            "néhány lépés, és már kész is az ajándék.",

        howItems: [
            {
                title:
                    "válassz összeget",

                description:
                    "add meg, mekkora értékű strike ajándékkártyát szeretnél.",
            },
            {
                title:
                    "vásárold meg online",

                description:
                    "add meg a szükséges adatokat, majd fizesd ki online.",
            },
            {
                title:
                    "megérkezik e-mailben vagy postán",

                description:
                    "a digitális ajándékkártyát a sikeres vásárlást követően e-mailben küldjük el. a fizikai ajándékkártyát pedig a vásárláskor megadott címre.",
            },
            {
                title:
                    "felhasználható foglaláskor",

                description:
                    "az ajándékozottnak foglaláskor csak be kell írnia az ajándékkártyán található kódot a kedvezménykód mezőbe.",
            },
        ],

        cardTypesTitle:
            "digitális vagy fizikai",

        cardTypesIntroduction:
            "a strike ajándékkártya digitális és fizikai változatban is létezik.",

        digitalCardTitle:
            "digitális ajándékkártya",

        digitalCardDescription:
            "online megvásárolható, és a sikeres fizetés után e-mailben küldjük el.",

        digitalCardStatus:
            "elérhető",

        physicalCardTitle:
            "fizikai ajándékkártya",

        physicalCardDescription:
            "kézbe adható strike ajándékkártya, amelyet postán küldünk el a vásárláskor megadott címre.",

        physicalCardStatus:
            "jelenleg elfogyott",

        occasionsTitle:
            "nem csak születésnapra",

        occasionsIntroduction:
            "akkor is jó választás, amikor egyszerűen valami élményt szeretnél adni.",

        occasionsItems: [
            "születésnapra",
            "karácsonyra",
            "névnapra",
            "meglepetésnek",
            "barátoknak",
            "kollégáknak",
        ],

        digitalTitle:
            "digitális, egyszerű, gyors",

        digitalFirst:
            "nem kell érte eljönnöd, és nem kell megvárnod a kiszállítást.",

        digitalSecond:
            "a sikeres vásárlást követően a digitális ajándékkártyát e-mailben küldjük el.",

        digitalThird:
            "így akár az utolsó pillanatban is lehet belőle ajándék.",

        validityTitle:
            "1 évig felhasználható",

        validityFirst:
            "az ajándékkártya a vásárlás napjától számított 1 évig érvényes.",

        validitySecond:
            "ez idő alatt az ajándékozott kiválaszthatja a számára megfelelő időpontot és felhasználhatja az ajándékkártyát a foglalása során.",

        importantTitle:
            "amit érdemes tudni",

        importantIntroduction:
            "néhány fontos információ az ajándékkártyáról.",

        importantItems: [
            "minimum 5 000 Ft értékben vásárolható",
            "digitális és fizikai változatban is létezik",
            "a digitális változatot e-mailben küldjük el",
            "a fizikai változatot postán küldjük el",
            "a vásárlástól számított 1 évig érvényes",
            "foglaláskor a kódot a kedvezménykód mezőbe kell beírni",
            "készpénzre nem váltható",
        ],

        finalTitle:
            "adj valami olyat, amit tényleg átélnek",

        finalDescription:
            "válassz egy összeget, és készítsd el a strike ajándékkártyát néhány lépésben.",

        finalButton:
            "ajándékkártyát vásárolok",
    },

    en: {
        heroTitle:
            "give the gift of strike",

        heroSubtitle:
            "when you want to give more than just another thing.",

        heroDescription:
            "with a strike gift card, you don't have to choose when they come, who they bring or which experience they want. they can decide for themselves.",

        heroSecond:
            "choose an amount, purchase it online and your gift is ready.",

        purchaseButton:
            "buy a gift card",

        giftTitle:
            "a gift that becomes an experience",

        giftFirst:
            "sometimes it's difficult to know what someone would really enjoy.",

        giftSecond:
            "with a strike gift card, you're not giving another object. you're giving them a chance to play, compete and enjoy an experience together.",

        giftThird:
            "and they can use it whenever the time is right for them.",

        howTitle:
            "how does it work?",

        howIntroduction:
            "just a few steps and your gift is ready.",

        howItems: [
            {
                title:
                    "choose an amount",

                description:
                    "enter the value of the strike gift card you'd like to purchase.",
            },
            {
                title:
                    "buy it online",

                description:
                    "enter the required details and complete the payment online.",
            },
            {
                title:
                    "receive it by e-mail",

                description:
                    "the digital gift card is sent by e-mail after the successful purchase.",
            },
            {
                title:
                    "redeem it for a booking",

                description:
                    "during booking, the recipient simply enters the code shown on the gift card in the discount code field.",
            },
        ],

        cardTypesTitle:
            "digital or physical",

        cardTypesIntroduction:
            "strike gift cards are available in both digital and physical formats.",

        digitalCardTitle:
            "digital gift card",

        digitalCardDescription:
            "available online and sent by e-mail after successful payment.",

        digitalCardStatus:
            "available",

        physicalCardTitle:
            "physical gift card",

        physicalCardDescription:
            "a physical strike gift card delivered by post to the address provided during purchase.",

        physicalCardStatus:
            "currently sold out",

        occasionsTitle:
            "not just for birthdays",

        occasionsIntroduction:
            "a great choice whenever you simply want to give someone an experience.",

        occasionsItems: [
            "birthdays",
            "christmas",
            "name days",
            "surprises",
            "friends",
            "colleagues",
        ],

        digitalTitle:
            "digital, simple and quick",

        digitalFirst:
            "there's no need to visit us or wait for delivery.",

        digitalSecond:
            "after a successful purchase, the digital gift card is sent by e-mail.",

        digitalThird:
            "which also makes it a great last-minute gift.",

        validityTitle:
            "valid for 1 year",

        validityFirst:
            "the gift card is valid for 1 year from the date of purchase.",

        validitySecond:
            "during this period, the recipient can choose a suitable time and redeem the gift card during their booking.",

        importantTitle:
            "good to know",

        importantIntroduction:
            "a few important details about strike gift cards.",

        importantItems: [
            "available from 5,000 HUF",
            "available in digital and physical formats",
            "the digital version is delivered by e-mail",
            "the physical version is delivered by post",
            "valid for 1 year from purchase",
            "enter the gift card code in the discount code field during booking",
            "cannot be redeemed for cash",
        ],

        finalTitle:
            "give them something they'll actually experience",

        finalDescription:
            "choose an amount and create your strike gift card in just a few steps.",

        finalButton:
            "buy a gift card",
    },
};

export default function GiftCardPage() {
    const {
        language,
    } =
        useLanguage();

    const text =
        translations[language] ||
        translations.hu;

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header />

            {/* HERO */}
            <section className="bg-[#dedede] text-black">
                <div className="mx-auto flex min-h-[500px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-14 text-center md:max-w-5xl md:min-h-[620px] md:px-12 md:py-20">
                    <IconCircle>
                        <Gift
                            style={{
                                width:
                                    "40px",

                                height:
                                    "40px",
                            }}
                            strokeWidth={
                                2.5
                            }
                        />
                    </IconCircle>

                    <h1 className="text-[30px] font-black lowercase leading-[1.05] md:text-[52px]">
                        {text.heroTitle}
                    </h1>

                    <p className="mt-7 max-w-[330px] text-[19px] font-black leading-[1.2] md:max-w-2xl md:text-[27px]">
                        {text.heroSubtitle}
                    </p>

                    <p className="mt-7 max-w-[325px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.45]">
                        {text.heroDescription}
                    </p>

                    <p className="mt-6 max-w-[325px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.45]">
                        {text.heroSecond}
                    </p>

                    <PrimaryButton
                        href="/ajandekkartya/vasarlas"
                    >
                        {text.purchaseButton}
                    </PrimaryButton>
                </div>
            </section>

            {/* AJÁNDÉKKÁRTYA VIZUÁL */}
            <GiftCardVisual
                language={
                    language
                }
            />

            {/* MIÉRT JÓ */}
            <ContentSection
                icon={
                    Sparkles
                }
                title={
                    text.giftTitle
                }
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.giftFirst}
                    </p>

                    <p>
                        {text.giftSecond}
                    </p>

                    <p>
                        {text.giftThird}
                    </p>
                </div>
            </ContentSection>

            {/* HOGYAN MŰKÖDIK */}
            <InfoListSection
                icon={
                    CreditCard
                }
                title={
                    text.howTitle
                }
                introduction={
                    text.howIntroduction
                }
                items={
                    text.howItems
                }
            />

            {/* AJÁNDÉKKÁRTYA TÍPUSOK */}
            <GiftCardTypesSection
                text={
                    text
                }
            />

            {/* DIGITÁLIS */}
            <ContentSection
                icon={
                    Mail
                }
                title={
                    text.digitalTitle
                }
                backgroundClass="bg-white"
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.digitalFirst}
                    </p>

                    <p>
                        {text.digitalSecond}
                    </p>

                    <p>
                        {text.digitalThird}
                    </p>
                </div>

                <PrimaryButton
                    href="/ajandekkartya/vasarlas"
                >
                    {text.purchaseButton}
                </PrimaryButton>
            </ContentSection>

            {/* ALKALMAK */}
            <ListSection
                icon={
                    Gift
                }
                title={
                    text.occasionsTitle
                }
                introduction={
                    text.occasionsIntroduction
                }
                items={
                    text.occasionsItems
                }
            />

            {/* ÉRVÉNYESSÉG */}
            <ContentSection
                icon={
                    CalendarDays
                }
                title={
                    text.validityTitle
                }
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.validityFirst}
                    </p>

                    <p>
                        {text.validitySecond}
                    </p>
                </div>
            </ContentSection>

            {/* FONTOS TUDNIVALÓK */}
            <ListSection
                icon={
                    Check
                }
                title={
                    text.importantTitle
                }
                introduction={
                    text.importantIntroduction
                }
                items={
                    text.importantItems
                }
            />

            {/* ZÁRÓ BLOKK */}
            <section className="bg-black text-white">
                <div className="mx-auto flex min-h-[390px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-16 text-center md:max-w-5xl md:min-h-[500px] md:px-12 md:py-20">
                    <Gift
                        className="h-12 w-12 md:h-16 md:w-16"
                        strokeWidth={
                            2.2
                        }
                    />

                    <h2 className="mt-7 max-w-[340px] text-[27px] font-black lowercase leading-[1.1] md:max-w-3xl md:text-[48px]">
                        {text.finalTitle}
                    </h2>

                    <p className="mt-7 max-w-[330px] text-[15px] leading-[1.4] text-white/70 md:max-w-2xl md:text-[20px]">
                        {text.finalDescription}
                    </p>

                    <PrimaryButton
                        href="/ajandekkartya/vasarlas"
                    >
                        {text.finalButton}
                    </PrimaryButton>
                </div>
            </section>

            <Footer />
        </main>
    );
}

function GiftCardVisual({
    language,
}) {
    return (
        <section className="bg-black">
            <div className="mx-auto flex w-full max-w-[430px] items-center justify-center px-5 py-16 md:max-w-5xl md:px-12 md:py-24">
                <div className="relative aspect-[1.62/1] w-full max-w-[680px] overflow-hidden rounded-[18px] bg-[#f5a400] p-7 text-black shadow-[0_30px_80px_rgba(0,0,0,0.5)] md:p-10">
                    <div className="absolute -right-14 -top-14 h-48 w-48 rounded-full border-[28px] border-black/10 md:h-64 md:w-64" />

                    <div className="absolute -bottom-24 -left-20 h-60 w-60 rounded-full border-[36px] border-black/10 md:h-72 md:w-72" />

                    <div className="relative z-10 flex h-full flex-col">
                        <div className="flex items-center justify-between">
                            <img
                                src="/v4/strike-logo-black-2.webp"
                                alt="strike"
                                className="h-auto w-[110px] object-contain md:w-[150px]"
                            />

                            <Gift
                                strokeWidth={
                                    2.5
                                }
                                className="h-7 w-7 md:h-9 md:w-9"
                            />
                        </div>

                        <div className="flex flex-1 flex-col justify-center">
                            <div className="text-[12px] font-black uppercase tracking-[0.18em] text-black/60 md:text-[15px]">
                                {language === "en"
                                    ? "GIFT CARD"
                                    : "AJÁNDÉKKÁRTYA"}
                            </div>

                            <div className="mt-2 max-w-[420px] text-[34px] font-black leading-[0.95] md:text-[54px]">
                                {language === "en"
                                    ? "give an experience"
                                    : "ajándékozz élményt"}
                            </div>
                        </div>

                        <div className="text-[11px] font-black uppercase tracking-[0.16em] text-black/55 md:text-[13px]">
                            strike.hu
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function GiftCardTypesSection({
    text,
}) {
    return (
        <section className="bg-[#dedede] text-black">
            <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                <div className="border-[3px] border-black bg-[#f2f2f2] px-5 py-10 md:px-12 md:py-14">
                    <div className="flex flex-col items-center text-center">
                        <IconCircle>
                            <Gift
                                style={{
                                    width:
                                        "38px",

                                    height:
                                        "38px",
                                }}
                                strokeWidth={
                                    2.5
                                }
                            />
                        </IconCircle>

                        <h2 className="text-[22px] font-black lowercase leading-[1.1] md:text-[40px]">
                            {text.cardTypesTitle}
                        </h2>

                        <p className="mt-6 max-w-[310px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-xl md:text-[19px] md:leading-[1.45]">
                            {text.cardTypesIntroduction}
                        </p>
                    </div>

                    <div className="mt-10 grid gap-4 md:mt-14 md:grid-cols-2">
                        <div className="relative flex flex-col border-[3px] border-black bg-[#e4e4e4] p-6 md:p-8">
                            <div className="flex items-start justify-between gap-4">
                                <Gift
                                    className="h-9 w-9"
                                    strokeWidth={
                                        2.5
                                    }
                                />

                                <span
                                    className="
                                        border
                                        border-black/20
                                        bg-black/10
                                        px-3
                                        py-1.5
                                        text-[10px]
                                        font-black
                                        uppercase
                                        tracking-[0.08em]
                                        text-black/55
                                        md:text-[11px]
                                    "
                                >
                                    {text.physicalCardStatus}
                                </span>
                            </div>

                            <h3 className="mt-6 text-[19px] font-black lowercase leading-[1.1] text-black/70 md:text-[24px]">
                                {text.physicalCardTitle}
                            </h3>

                            <p className="mt-4 text-[14px] leading-[1.5] text-neutral-500 md:text-[17px]">
                                {text.physicalCardDescription}
                            </p>
                        </div>

                        <div className="flex flex-col border-[3px] border-[#f5a400] bg-white p-6 md:p-8">
                            <div className="flex items-start justify-between gap-4">
                                <Mail
                                    className="h-9 w-9"
                                    strokeWidth={
                                        2.5
                                    }
                                />

                                <span
                                    className="
                                        bg-[#f5a400]
                                        px-3
                                        py-1.5
                                        text-[10px]
                                        font-black
                                        uppercase
                                        tracking-[0.08em]
                                        text-black
                                        md:text-[11px]
                                    "
                                >
                                    {text.digitalCardStatus}
                                </span>
                            </div>

                            <h3 className="mt-6 text-[19px] font-black lowercase leading-[1.1] md:text-[24px]">
                                {text.digitalCardTitle}
                            </h3>

                            <p className="mt-4 flex-1 text-[14px] leading-[1.5] text-neutral-700 md:text-[17px]">
                                {text.digitalCardDescription}
                            </p>

                            <PrimaryButton
                                href="/ajandekkartya/vasarlas"
                            >
                                {text.purchaseButton}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function IconCircle({
    children,
}) {
    return (
        <div
            className="mb-6 flex shrink-0 items-center justify-center rounded-full border-[3px] border-black"
            style={{
                width:
                    "80px",

                height:
                    "80px",

                minWidth:
                    "80px",

                maxWidth:
                    "80px",

                minHeight:
                    "80px",

                maxHeight:
                    "80px",

                boxSizing:
                    "border-box",
            }}
        >
            {children}
        </div>
    );
}

function ContentSection({
    icon: Icon,
    title,
    children,
    backgroundClass = "bg-[#dedede]",
}) {
    return (
        <section className={`${backgroundClass} text-black`}>
            <div className="mx-auto flex min-h-[470px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-14 text-center md:max-w-5xl md:min-h-[590px] md:px-12 md:py-20">
                <IconCircle>
                    <Icon
                        style={{
                            width:
                                "38px",

                            height:
                                "38px",
                        }}
                        strokeWidth={
                            2.5
                        }
                    />
                </IconCircle>

                <h2 className="text-[22px] font-black lowercase leading-[1.1] md:text-[38px]">
                    {title}
                </h2>

                {children}
            </div>
        </section>
    );
}

function InfoListSection({
    icon: Icon,
    title,
    introduction,
    items,
}) {
    return (
        <section className="bg-[#f2f2f2] text-black">
            <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                <div className="border-[3px] border-black bg-[#f2f2f2]">
                    <div className="bg-black px-6 py-9 text-center text-white md:px-12 md:py-12">
                        <Icon
                            className="mx-auto h-10 w-10 md:h-12 md:w-12"
                            strokeWidth={
                                2.5
                            }
                        />

                        <h2 className="mt-5 text-[24px] font-black lowercase leading-none md:text-[42px]">
                            {title}
                        </h2>

                        <p className="mx-auto mt-5 max-w-[300px] text-[14px] leading-[1.3] text-white/70 md:max-w-xl md:text-[19px]">
                            {introduction}
                        </p>
                    </div>

                    <div className="px-5 py-8 md:px-12 md:py-12">
                        <div className="border-y-[3px] border-black">
                            {items.map(
                                (
                                    item,
                                    index
                                ) => (
                                    <div
                                        key={
                                            item.title
                                        }
                                        className={`flex items-start gap-4 py-5 md:gap-6 md:py-7 ${
                                            index !==
                                            items.length - 1
                                                ? "border-b border-black/25"
                                                : ""
                                        }`}
                                    >
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-[11px] font-black text-white md:h-11 md:w-11 md:text-[13px]">
                                            {String(
                                                index +
                                                1
                                            ).padStart(
                                                2,
                                                "0"
                                            )}
                                        </span>

                                        <div>
                                            <h3 className="text-[16px] font-black lowercase leading-[1.2] md:text-[21px]">
                                                {
                                                    item.title
                                                }
                                            </h3>

                                            <p className="mt-2 text-[14px] leading-[1.4] text-neutral-700 md:text-[18px] md:leading-[1.5]">
                                                {
                                                    item.description
                                                }
                                            </p>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ListSection({
    icon: Icon,
    title,
    introduction,
    items,
}) {
    return (
        <section className="bg-[#dedede] text-black">
            <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                <div className="border-[3px] border-black bg-[#f2f2f2] px-5 py-10 md:px-12 md:py-14">
                    <div className="flex flex-col items-center text-center">
                        <IconCircle>
                            <Icon
                                style={{
                                    width:
                                        "38px",

                                    height:
                                        "38px",
                                }}
                                strokeWidth={
                                    2.5
                                }
                            />
                        </IconCircle>

                        <h2 className="text-[22px] font-black lowercase leading-[1.1] md:text-[40px]">
                            {title}
                        </h2>

                        <p className="mt-6 max-w-[310px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-xl md:text-[19px] md:leading-[1.45]">
                            {introduction}
                        </p>
                    </div>

                    <div className="mt-10 border-y-[3px] border-black md:mt-14">
                        {items.map(
                            (
                                item,
                                index
                            ) => (
                                <div
                                    key={
                                        item
                                    }
                                    className={`flex items-center gap-4 py-5 md:gap-6 md:py-6 ${
                                        index !==
                                        items.length - 1
                                            ? "border-b border-black/25"
                                            : ""
                                    }`}
                                >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-[11px] font-black text-white md:h-11 md:w-11 md:text-[13px]">
                                        {String(
                                            index +
                                            1
                                        ).padStart(
                                            2,
                                            "0"
                                        )}
                                    </span>

                                    <p className="text-[15px] font-semibold leading-[1.4] text-neutral-700 md:text-[19px]">
                                        {item}
                                    </p>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function PrimaryButton({
    href,
    children,
}) {
    return (
        <a
            href={
                href
            }
            className="
                mt-9 flex h-[52px]
                min-w-[240px]
                cursor-pointer select-none
                items-center justify-center
                rounded-[6px]
                bg-[#f5a400] px-7
                text-[18px] font-black
                text-black transition-transform
                duration-200
                hover:scale-[1.03]
                active:translate-y-[2px]
                active:scale-95
                md:mt-11 md:h-[60px]
                md:min-w-[280px]
                md:text-[21px]
            "
        >
            {children}
        </a>
    );
}
